import Link from 'next/link'
import { useMemo } from 'react'
import { View } from 'react-native'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import { body2 } from '@nxt-insurance/ui-kit/core/typography'
import type { Color } from '@nxt-insurance/ui-kit/types'

import { webUrlSchema } from '@/cms/webUrl'
import { EVENTS, LINKS_IDS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import type { PortableTextComponentProps } from '@portabletext/react'

const footerListSchema = z.object({
  _type: z.literal('footerLinkList'),
  _key: z.string(),
  entries: z.array(
    z.object({
      _key: z.string(),
      label: z.string(),
      openCookieSettings: z.boolean().optional(),
      target: webUrlSchema.max(1).optional(),
    })
  ),
})

type FooterLinkList = z.infer<typeof footerListSchema>

const FooterLinkElement = (props: {
  sectionName: string
  link: FooterLinkList['entries'][number]
  className: string
}) => {
  const { track } = useAnalytics()
  const { link } = props
  const href = useUrlFromWebUrlReference(link.target)
  return (
    <Link
      className={props.className}
      href={href}
      scroll={Boolean(link.target)}
      shallow={!link.target}
      target={href.includes('.pdf') ? '_blank' : undefined}
      rel={href.includes('.pdf') ? 'noopener noreferrer nofollow' : undefined}
      onClick={() => {
        track(EVENTS.LinkClicked, {
          href,
          children: link.label,
          sectionName: props.sectionName,
        })
      }}
    >
      {link.label}
    </Link>
  )
}

const footerLinkStyles = (textColor?: Color) => {
  const makeStyles = tv({
    slots: {
      container: 'flex flex-col',
      link: [body2({ textColor }), 'my-1.5 block transition duration-300 hover:opacity-70'],
    },
  })
  return makeStyles()
}

export const FooterLinkList = (props: {
  node: PortableTextComponentProps<unknown>
  sectionName: string
  pageColor?: Color
}) => {
  const value = useMemo(() => {
    return footerListSchema.parse(props.node.value)
  }, [props.node.value])
  const { track } = useAnalytics()
  const styles = footerLinkStyles(props.pageColor ?? 'brandDark')

  const showCookieSettings = () => {
    window.CookieFirst?.openPanel()
    track(EVENTS.LinkClicked, {
      linkId: LINKS_IDS.FOOTER_COOKIE_SETTINGS,
    })
  }

  return (
    <View className={styles.container()}>
      {value.entries.map((link) => {
        if (link.openCookieSettings) {
          return (
            <button key={link._key} className={styles.link()} onClick={showCookieSettings}>
              {link.label}
            </button>
          )
        }
        return (
          <FooterLinkElement
            key={link._key}
            sectionName={props.sectionName}
            link={link}
            className={styles.link()}
          />
        )
      })}
    </View>
  )
}
